/**
 * Raiz de la aplicacion: sesion -> tenencia -> ruta.
 *
 * El orden importa y es el mismo de siempre: sin sesion no hay token, sin token
 * no se puede preguntar la tenencia, y sin tienda confirmada una pantalla
 * acotada no tiene nada que pedir.
 *
 * Por que hay un router, y por que este
 * -------------------------------------
 * Con dos pantallas alcanzaba un `useState`. Con diez ya no: sin URLs no se
 * puede compartir un link, ni recargar y volver al mismo lugar, ni usar el
 * boton "atras" -- y este panel reemplaza a un GUI de escritorio que la gente
 * usa todo el dia.
 *
 * Se eligio **react-router-dom v7 en modo declarativo** (`<BrowserRouter>` +
 * `<Routes>`), y no las otras dos opciones razonables:
 *
 * - **Un router propio de 80 lineas** (History API + un `useSyncExternalStore`).
 *   Es tentador y arranca bien, pero termina reimplementando mal lo que ya
 *   funciona: `NavLink` con estado activo, redirecciones declarativas,
 *   navegacion relativa, `useSearchParams`. Es codigo nuestro para mantener a
 *   cambio de nada propio.
 * - **El modo "data" de v7** (`createBrowserRouter` + loaders). Sus loaders
 *   corren FUERA del arbol de React, asi que no pueden leer el `ApiClient` que
 *   `AuthProvider` publica por contexto -- justamente el que sabe refrescar el
 *   token. Adoptarlo obligaria a sacar el cliente HTTP del contexto o a
 *   duplicarlo. El modo declarativo deja la carga de datos donde ya esta
 *   (`useAsync` adentro de cada pantalla) y no pelea con nada.
 *
 * Requisito de infraestructura, ya cumplido: `<BrowserRouter>` usa la History
 * API, o sea URLs limpias sin `#`, y eso necesita que el server devuelva
 * `index.html` en cualquier path. `web/public/_redirects` ya trae
 * `/*  /index.html  200` (rewrite, no redirect: mantiene la URL). Por eso no se
 * uso `<HashRouter>`, que no necesitaria nada del server pero deja URLs como
 * `/#/facturacion`.
 */

import { Route, Routes } from "react-router-dom";

import { useAuth } from "./auth/AuthProvider";
import { LoginPage } from "./auth/LoginPage";
import { AppLayout } from "./components/AppLayout";
import { AfterLogin, RedirectToLogin } from "./components/AuthRoutes";
import { Spinner } from "./components/Feedback";
import { NotFound } from "./components/NotFound";
import { SECTIONS } from "./components/routes";
import { StoreAccessProvider } from "./stores/StoreAccessProvider";

export function App() {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <main className="app__boot">
        <Spinner label="Restaurando sesion..." />
      </main>
    );
  }

  // Sin sesion el arbol autenticado ni siquiera se declara: no hay ruta que
  // pueda montar una pantalla protegida.
  if (session === null) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    );
  }

  return (
    // El provider esta ARRIBA del router: la tienda elegida es del usuario, no
    // de la pantalla. Navegar no lo remonta, asi que la seleccion no se
    // reinicia ni se vuelve a resolver la tenencia en cada seccion.
    <StoreAccessProvider>
      <Routes>
        <Route path="/login" element={<AfterLogin />} />
        <Route element={<AppLayout />}>
          {SECTIONS.map((section) =>
            section.path === "/" ? (
              <Route key={section.id} index element={section.element} />
            ) : (
              <Route key={section.id} path={section.path.slice(1)} element={section.element} />
            ),
          )}
          {/* Dentro del layout a proposito: un 404 con el menu al lado deja
              salir de ahi sin tocar la URL a mano. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </StoreAccessProvider>
  );
}
